import { html, LitElement, PropertyValues } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';
import { classMap } from 'lit/directives/class-map.js';
import { IClassMap } from '../helpers';
import { AtlAccordionClickEvent, AtlAccordionCollapseEvent } from './accordion.props';

import * as TagNames from './accordion.tagnames';

type CollapseType = { 
    toggle: () => void;
    show: () => void;
    hide: () => void;
};

@customElement(TagNames.AccordionItem)
export class AtlAccordionItem extends LitElement {

    declare accordionTitle: string;
    declare show?: boolean;
    declare first?: boolean;
    declare last?: boolean;
    declare disabled?: boolean;
    declare defaultOpen?: boolean;

    declare _isOpen?: boolean;

    private _collapseItem: CollapseType;

    @query('.accordion-collapse') 
    private _accordionCollapse?: HTMLElement;

    static properties = {
        accordionTitle: { attribute: 'accordion-title', type: String },
        show: { type: Boolean },
        first: { type: Boolean },
        last: { type: Boolean },
        disabled: { type: Boolean, state: false },
        _isOpen: { type: Boolean, state: true },
        defaultOpen: { attribute: 'default-open', type: Boolean }
    };

    static styles = [
        ...getGlobalStyleSheets()
    ];

    constructor() {
        super();
        this._isOpen = false;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        
        this._collapseItem = new bootstrap.Collapse(this._accordionCollapse, { toggle: false });
        if (this.show || this.defaultOpen === true) {
            this._onButtonClick();
        }
    }

    override updated(_changedProperties: PropertyValues) {
        super.updated(_changedProperties);
        if (_changedProperties.has('show')) {
            if (this.show && !this._isOpen) {
                this._onButtonClick();
            }
        }
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener(AtlAccordionCollapseEvent.EVENT_NAME, this._handleAccordionItemCollapse);
    }

    disconnectedCallback(): void {        
        this.removeEventListener(AtlAccordionCollapseEvent.EVENT_NAME, this._handleAccordionItemCollapse);
        super.disconnectedCallback();
    }
    
    render() {
        const accordionItemClass: IClassMap = {
            "accordion-item": true,
            "first": this.first === true,
            "last": this.last === true
        }

        const buttonClass: IClassMap = {
            "btn": true,
            "accordion-button": true,
            "collapsed": !this._isOpen
        };

        const accordionClass: IClassMap = {
            "accordion-collapse": true,
            "collapse": true
        }

        return html`
            <div class=${classMap(accordionItemClass)}>
                <h2 class="accordion-header">
                    <button class=${classMap(buttonClass)}
                        type="button"
                        ?disabled=${this.disabled}
                        @click=${this._onButtonClick}
                    >
                        <span>${this.accordionTitle}</span>
                        <slot name="header"></slot>
                    </button>
                </h2>
                <div class=${classMap(accordionClass)}>
                    <div class="accordion-body">
                        <slot></slot>
                    </div>
                </div>
            </div>
        `;
    }

    private _onButtonClick = () => {        
        this._isOpen = !this._isOpen;
        this._collapseItem.toggle();
        
        this.dispatchEvent(new AtlAccordionClickEvent({ title: this.accordionTitle, opened: this._isOpen }));
    }

    private _handleAccordionItemCollapse = (event: Event) => {
        const customEvent = event as AtlAccordionCollapseEvent;

        if (customEvent.detail.title === this.accordionTitle) return;

        this._isOpen = false;
        this._collapseItem.hide();
    };
}